import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { PatternFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Avatar from 'components/atomic/Avatar';
import Icon from 'components/atomic/Icon';

// eslint-disable-next-line react/display-name, react/prop-types, no-unused-vars
const StyledContainer = styled(forwardRef(({ active, ...props }, ref) => <div ref={ref} {...props} />))`
  display: grid;
  row-gap: ${theme.spaces.regular}px;
  padding: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 16px;
  background: ${theme.colors.linkWater};

  .user-item {
    display: grid;
    column-gap: ${theme.spaces.smallX}px;
    row-gap: ${theme.spaces.small2X}px;
    grid-column-start: 1;
    grid-column-end: 2;
    justify-self: start;

    > div {
      grid-row-start: 1;
      grid-row-end: 2;

      &:first-child {
        grid-row-start: 1;
        grid-row-end: 3;
      }

      &:last-child {
        grid-row-start: 2;
        grid-row-end: 3;
      }
    }
  }

  .action-item {
    display: grid;
    gap: ${theme.spaces.smallX}px;
    grid-column-start: 2;
    grid-column-end: 3;
    justify-self: end;

    > * {
      grid-row-start: 1;
      grid-row-end: 3;
    }
  }

  .info-item {
    display: flex;
    align-items: center;
    grid-column-start: 1;
    grid-column-end: 3;
    height: 20px;
    font-family: 'Inter';
    font-size: ${theme.fontSizes.small}px;
    font-weight: 400;
    line-height: 16px;
    vertical-align: middle;
    color: ${theme.colors.gray.secondary};

    > * {
      display: flex;
      align-items: center;

      span:first-child {
        margin-right: ${theme.spaces.smallX}px;
      }
    }
  }

  ${({ active }) =>
    active &&
    css`
      background: ${theme.colors.white};
    `}
`;

const StyledRole = styled.div`
  font-family: 'Inter';
  font-size: ${theme.fontSizes.small}px;
  font-weight: 600;
  line-height: 16px;
  color: ${theme.colors.gray.secondary};
`;

const StyledName = styled.div`
  font-family: 'Inter';
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 600;
  line-height: 24px;
  color: ${theme.colors.gray.primary};
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 40px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 50%;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;
`;

const StyledSeparator = styled.span`
  display: inline-block;
  height: 4px;
  width: 4px;
  margin-left: ${theme.spaces.small}px;
  margin-right: ${theme.spaces.small}px;
  border-radius: 50%;
  background: ${theme.colors.gray.secondary};
`;

const ContactCard = ({ active, avatarUrl, name, role, email, phone, ...restProps }) => {
  return (
    <StyledContainer active={active} {...restProps}>
      <div className="user-item">
        <Avatar
          url={avatarUrl}
          name={name}
          textColor={theme.colors.indigo.primary}
          bgColor={theme.colors.indigo.superLight}
        />
        <StyledRole>{role}</StyledRole>
        <StyledName>{name}</StyledName>
      </div>
      <div className="action-item">
        <a href={`tel:${phone}`}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.call} color={theme.colors.gray.secondary} />
          </StyledIconContainer>
        </a>
        <a href={`mailto:${email}`}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.sms} color={theme.colors.gray.secondary} />
          </StyledIconContainer>
        </a>
        <Link to="../support">
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.message} color={theme.colors.gray.secondary} height={20} width={20} />
          </StyledIconContainer>
        </Link>
      </div>
      <div className="info-item">
        <span>
          <span>
            <Icon alias={ICON_ALIASES.calling} color={theme.colors.gray.secondary} />
          </span>
          <PatternFormat
            allowEmptyFormatting
            displayType="text"
            format="(###) ###-#### ext ###"
            mask="X"
            value={phone.substring(2)}
          />
        </span>
        <StyledSeparator />
        <span>
          <span>
            <Icon alias={ICON_ALIASES.sms} color={theme.colors.gray.secondary} />
          </span>
          {email}
        </span>
      </div>
    </StyledContainer>
  );
};

ContactCard.propTypes = {
  active: PropTypes.bool,
  avatarUrl: PropTypes.string,
  name: PropTypes.string,
  role: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
};

export default ContactCard;
